import type { Hotspot, Stage } from '../../data/notationHotspots';
import { layerInfo } from '../../data/notationHotspots';
import { VizRouter } from './vizzes/VizRouter';
import './NotationPanel.css';

interface NotationPanelProps {
  hotspot: Hotspot | null;
  onClose: () => void;
  onSelectRelated: (id: string) => void;
  allHotspots: Hotspot[];
  currentStage: Stage;
}

export function NotationPanel({ hotspot, onClose, onSelectRelated, allHotspots, currentStage }: NotationPanelProps) {
  if (!hotspot) return null;

  const layer = layerInfo[hotspot.layer];
  const relatedHotspots = hotspot.relatedIds
    ?.map(id => allHotspots.find(h => h.id === id))
    .filter((h): h is Hotspot => h !== undefined && h.stage <= currentStage);

  return (
    <div className={`notation-panel ${hotspot ? 'notation-panel--open' : ''}`}>
      <button className="notation-panel__close" onClick={onClose} aria-label="Close panel">
        <i className="fas fa-times"></i>
      </button>

      <div className="notation-panel__content">
        <header className="notation-panel__header">
          <div
            className="notation-panel__icon"
            style={{ background: layer.color }}
          >
            <i className={`fas ${hotspot.icon}`}></i>
          </div>
          <div>
            <span className="notation-panel__layer" style={{ color: layer.color }}>
              {layer.name}
            </span>
            <h2 className="notation-panel__title">{hotspot.label}</h2>
            <p className="notation-panel__short">{hotspot.short}</p>
          </div>
        </header>

        <section className="notation-panel__section notation-panel__section--excerpt">
          <blockquote>{hotspot.content.excerpt}</blockquote>
        </section>

        <section className="notation-panel__section">
          <h3><i className="fas fa-graduation-cap"></i> Teaching</h3>
          <p>{hotspot.content.teaching}</p>
        </section>

        {/* Stage-specific D3 visualization */}
        {hotspot.viz && (
          <section className="notation-panel__section notation-panel__section--viz">
            <VizRouter type={hotspot.viz.type} props={hotspot.viz.props} />
          </section>
        )}

        {hotspot.content.tryThis && (
          <section className="notation-panel__section notation-panel__section--try">
            <h3><i className="fas fa-lightbulb"></i> Try This</h3>
            <p>{hotspot.content.tryThis}</p>
          </section>
        )}

        {/* Stage 4 cost/benefit section */}
        {currentStage === 4 && (
          <section className="notation-panel__section notation-panel__section--cost-benefit">
            <h3><i className="fas fa-balance-scale"></i> What this notation gives you</h3>
            <p>{getNotationGives(hotspot)}</p>
            <h3 style={{ marginTop: 'var(--spacing-md)' }}><i className="fas fa-exclamation-triangle"></i> What it costs you</h3>
            <p>{getNotationCosts(hotspot)}</p>
          </section>
        )}

        <div className="notation-panel__core-message">
          <i className="fas fa-exclamation-circle"></i>
          <p>Notation is not the enemy. <strong>Unnoticed notation is.</strong></p>
        </div>

        {relatedHotspots && relatedHotspots.length > 0 && (
          <section className="notation-panel__section">
            <h3><i className="fas fa-project-diagram"></i> Connected Notations</h3>
            <div className="notation-panel__related">
              {relatedHotspots.map(related => (
                <button
                  key={related.id}
                  className="notation-panel__related-btn"
                  onClick={() => onSelectRelated(related.id)}
                  style={{ borderColor: layerInfo[related.layer].color }}
                >
                  <span
                    className="notation-panel__related-dot"
                    style={{ background: layerInfo[related.layer].color }}
                  />
                  <i className={`fas ${related.icon}`}></i>
                  <span>{related.label}</span>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function getNotationGives(hotspot: Hotspot): string {
  const gives: Record<string, string> = {
    'brain-sensory': 'Speed, prediction, pattern recognition. The ability to act before full understanding.',
    'sight': 'Object recognition, navigation, rapid categorization of the world.',
    'hearing': 'Social connection, language, threat detection through sound.',
    'smell': 'Instant recall, emotional memory, ancestral safety signals.',
    'taste': 'Nourishment detection, pleasure, reward-driven learning.',
    'touch': 'Physical boundaries, safety assessment, grounding in the present.',
    'interoception': 'Emotional awareness, self-regulation signals, body wisdom.',
    'balance': 'Physical stability, spatial confidence, grounded action.',
    'eyes-integration': 'Focused attention, priority-setting, selective awareness.',
    'ears-integration': 'Social belonging, threat detection, empathic connection.',
    'mouth-integration': 'Narrative coherence, identity stability, communicable meaning.',
    'hands-integration': 'Agency, problem-solving, the capacity to shape the world.',
    'chest-integration': 'Value clarity, motivation, emotional guidance for decisions.',
    'gut-integration': 'Fast survival responses, urgency signals, action readiness.',
    'feet-integration': 'Rootedness, belonging, sense of place and home.',
    'head-integration': 'Continuity of self, mental models, coherent worldview.',
    'phone': 'Connection, information access, coordination across distance.',
    'calendar': 'Coordination, planning, shared time structures.',
    'dollar': 'Exchange, coordination of labor, economic participation.',
    'chalkboard': 'Precision, proof, technological capability, clear reasoning.',
    'book': 'Transmissible meaning, cultural continuity, emotional education.',
    'checklist': 'Reliability, error reduction, coordination of complex tasks.',
    'compass': 'Direction, motivation, purposeful action.',
    'mirror': 'Self-awareness, social calibration, identity formation.',
    'social-feed': 'Connection, visibility, belonging, cultural participation.',
  };
  return gives[hotspot.id] || 'A way of organizing experience that enables action.';
}

function getNotationCosts(hotspot: Hotspot): string {
  const costs: Record<string, string> = {
    'brain-sensory': 'Accuracy. The brain fills gaps with expectations, not reality. You see what you expect.',
    'sight': 'Complexity. What is unseen becomes unreal. The visible dominates the meaningful.',
    'hearing': 'Nuance. Tone overrides content. Social decoding replaces careful listening.',
    'smell': 'Present accuracy. Past emotions flood the present without updating.',
    'taste': 'Freedom from craving. Preference hardens into compulsion and identity.',
    'touch': 'Openness to the intangible. What can\'t be grasped feels less real.',
    'interoception': 'Equanimity. Body signals escalate when ignored, dominate when over-identified with.',
    'balance': 'Openness to disorientation. Stability feels like truth; instability feels like error.',
    'eyes-integration': 'Peripheral awareness. What attention skips disappears from your world.',
    'ears-integration': 'Accurate assessment. Belonging anxiety distorts what you actually hear.',
    'mouth-integration': 'Flexibility. Repeated narratives become prison walls for identity.',
    'hands-integration': 'Acceptance. When agency is identity, inability to fix feels like failure.',
    'chest-integration': 'Discernment. Emotion freezes into judgment, ruling instead of informing.',
    'gut-integration': 'Calm assessment. Modern abstraction triggers ancient survival responses.',
    'feet-integration': 'Mobility. Place becomes destiny rather than context.',
    'head-integration': 'Truth. The mind protects its model at the expense of accurate perception.',
    'phone': 'Sustained attention. Manufactured urgency becomes indistinguishable from real need.',
    'calendar': 'Present-moment experience. Time becomes moral judgment; rest becomes guilt.',
    'dollar': 'Non-monetary value. What can\'t be priced becomes invisible and unjustifiable.',
    'chalkboard': 'Context. The measurable displaces the meaningful in human affairs.',
    'book': 'Direct experience. Inherited narratives become unexamined templates for living.',
    'checklist': 'Judgment. Binary completion replaces nuanced understanding of quality.',
    'compass': 'Fluidity. Direction becomes prison when treated as destiny.',
    'mirror': 'Becoming. The fixed image replaces the living process of who you are.',
    'social-feed': 'Self-acceptance. Comparison against edited highlights erodes inner stability.',
  };
  return costs[hotspot.id] || 'A narrowing of awareness that may go unnoticed.';
}
